<template>
  <VModal disable-backdrop-click @close="$emit('close')">
    <template #default="modalProps">
      <div class="rounded shadow-xs w-full max-w-lg mx-auto">
        <div class="rounded shadow-lg bg-white overflow-hidden flex flex-col">
          <div class="px-4 sm:px-6 py-5 border-b">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <h1 class="text-sm">{{ $t('accountContactCreateModal.title') }}</h1>
              </div>

              <div class="flex-none">
                <button
                  type="button"
                  class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="modalProps.close"
                >
                  <span class="sr-only">Close</span>
                  <svg
                    class="inline-flex h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <BaseVeeForm :action="handleSubmit" class="p-4 sm:p-6" @success="modalProps.close">
            <!-- formProps
              isIdle, isPending, isResolved, isRejected, invalid
              errors, submit
            -->
            <template #default="formProps">
              <div class="space-y-8">
                <div class="space-y-6">
                  <BaseFormTextFieldWithValidator
                    v-model="formData.firstName"
                    v-bind="firstNameFieldProps"
                    :disabled="formProps.isPending"
                    class="max-w-xs"
                  />
                  <BaseFormTextFieldWithValidator
                    v-model="formData.lastName"
                    v-bind="lastNameFieldProps"
                    :disabled="formProps.isPending"
                    class="max-w-xs"
                  />
                  <BaseFormTextFieldWithValidator
                    ref="emailAddressInputProvider"
                    v-model="formData.emailAddress"
                    v-bind="emailAddressFieldProps"
                    :disabled="formProps.isPending"
                    class="max-w-lg"
                  />
                  <BaseFormTextFieldWithValidator
                    ref="phoneNumberInputProvider"
                    v-model="formData.phoneNumber"
                    v-bind="phoneNumberFieldProps"
                    :disabled="formProps.isPending"
                    class="max-w-xs"
                  />
                </div>
                <div v-if="hasDuplicateContacts" class="space-y-8">
                  <div class="rounded-md bg-yellow-50 p-4">
                    <div class="flex">
                      <div class="flex-shrink-0">
                        <svg
                          class="h-5 w-5 text-yellow-400"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            fill-rule="evenodd"
                            d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </div>
                      <div class="ml-3">
                        <h3 class="text-sm font-medium text-yellow-800">
                          {{ $t('accountContactCreateModal.possibleDuplicateNotice.title') }}
                        </h3>
                        <div class="mt-2 text-sm text-yellow-800">
                          <i18n
                            v-if="existingContactByEmailAddress"
                            path="accountContactCreateModal.possibleDuplicateNotice.listItem"
                            tag="p"
                          >
                            <template v-slot:contact>
                              <a
                                role="button"
                                @click="showContact(existingContactByEmailAddress.id)"
                                class="font-medium hover:underline text-yellow-700 hover:text-yellow-900"
                                >{{ existingContactByEmailAddress.full_name }}</a
                              >
                            </template>
                            <template v-slot:type>
                              <span class="font-medium">{{
                                $t('accountContactCreateModal.emailLabel')
                              }}</span>
                            </template>
                          </i18n>
                          <i18n
                            v-if="existingContactByPhoneNumber"
                            path="accountContactCreateModal.possibleDuplicateNotice.listItem"
                            tag="p"
                          >
                            <template v-slot:contact>
                              <a
                                role="button"
                                @click="showContact(existingContactByPhoneNumber.id)"
                                class="font-medium hover:underline text-yellow-700 hover:text-yellow-900"
                                >{{ existingContactByPhoneNumber.full_name }}</a
                              >
                            </template>
                            <template v-slot:type>
                              <span class="font-medium">{{
                                $t('accountContactCreateModal.phoneLabel')
                              }}</span>
                            </template>
                          </i18n>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="flex justify-between">
                    <BaseButton
                      type="button"
                      primary
                      outline
                      :disabled="formProps.isPending"
                      @click="modalProps.close()"
                    >
                      {{ $t('accountContactCreateModal.cancel') }}
                    </BaseButton>
                    <BaseButton
                      primary
                      :disabled="formProps.isPending || formProps.invalid"
                      type="button"
                      @click="formProps.submit(true)"
                    >
                      {{ $t('accountContactCreateModal.submit') }}
                    </BaseButton>
                  </div>
                </div>
                <div v-else class="flex justify-between">
                  <BaseButton
                    type="button"
                    primary
                    outline
                    :disabled="formProps.isPending"
                    @click="modalProps.close()"
                  >
                    {{ $t('accountContactCreateModal.cancel') }}
                  </BaseButton>
                  <BaseButton primary :disabled="formProps.isPending || formProps.invalid">
                    {{ $t('accountContactCreateModal.checkAndSubmit') }}
                  </BaseButton>
                </div>
              </div>
            </template>
          </BaseVeeForm>
        </div>
      </div>
    </template>
  </VModal>
</template>

<script>
import { pickBy } from 'lodash-es';
import { accountContactsClient } from '@/api/clients';

export default {
  name: 'CreateContactDialog',
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      formData: {
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: '',
      },
      existingContactByEmailAddress: null,
      existingContactByPhoneNumber: null,
      ignoreDuplicates: false,
    };
  },
  computed: {
    firstNameFieldProps() {
      return {
        validator: {
          name: 'first name',
          vid: 'firstName',
          rules: 'required',
        },
        field: {
          id: 'contact_first_name',
          label: this.$t('accountContactCreateModal.firstNameLabel'),
        },
      };
    },
    lastNameFieldProps() {
      return {
        validator: {
          name: 'last name',
          vid: 'lastName',
          rules: { required: false },
        },
        field: {
          id: 'contact_last_name',
          label: this.$t('accountContactCreateModal.lastNameLabel'),
        },
      };
    },
    emailAddressFieldProps() {
      return {
        validator: {
          name: 'email address',
          vid: 'emailAddress',
          rules: {
            email: true,
            isRequiredWithout: { otherValue: '@phoneNumber', otherName: 'phone number' },
          },
        },
        field: {
          id: 'contact_email_address',
          label: this.$t('accountContactCreateModal.emailLabel'),
          inputType: 'email',
        },
      };
    },
    phoneNumberFieldProps() {
      return {
        validator: {
          name: 'phone number',
          vid: 'phoneNumber',
          rules: {
            e164phone: true,
            isRequiredWithout: { otherValue: '@emailAddress' },
          },
        },
        field: {
          id: 'contact_phone_number',
          label: this.$t('accountContactCreateModal.phoneLabel'),
        },
      };
    },
    hasDuplicateContacts() {
      return [this.existingContactByEmailAddress, this.existingContactByPhoneNumber].some(Boolean);
    },
  },
  watch: {
    'formData.emailAddress': {
      handler() {
        this.resetChecks();
      },
    },
    'formData.phoneNumber': {
      handler() {
        this.resetChecks();
      },
    },
  },
  methods: {
    resetChecks() {
      this.existingContactByEmailAddress = this.existingContactByPhoneNumber = null;
    },
    async duplicateRecordExists() {
      const checks = [];

      if (this.formData.emailAddress) {
        checks.push(
          this.findContact({
            filter: {
              email_address: this.formData.emailAddress,
            },
          })
        );
      }

      if (this.formData.phoneNumber) {
        checks.push(
          this.findContact({
            filter: {
              phone_number: this.formData.phoneNumber,
            },
          })
        );
      }

      const result = await Promise.all(checks);
      this.existingContactByEmailAddress = result?.[0] || null;
      this.existingContactByPhoneNumber = result?.[1] || null;

      if (result.filter(Boolean).length > 0) {
        return true;
      }

      return Promise.resolve(false);
    },
    async findContact(query) {
      try {
        const response = await accountContactsClient.list(this.accountId, query);

        return response.data?.[0] || null;
      } catch (error) {
        return null;
      }
    },
    showContact(contactId) {
      this.$modal.show({
        component: 'ContactModal',
        props: {
          contactId,
        },
        on: {
          'contact-deleted': () => {
            this.resetChecks();
          },
        },
        hideEvent: 'hide',
      });
    },
    async handleSubmit(ignoreDuplicates = false) {
      this.existingContactByEmailAddress = this.existingContactByPhoneNumber = null;
      if (ignoreDuplicates === false && (await this.duplicateRecordExists()) === true) {
        return Promise.reject(new Error('Duplicate records.'));
      }

      return accountContactsClient
        .store(this.accountId, pickBy(this.formData, Boolean))
        .then((response) => {
          this.$emit('created', response.data.id);
          return Promise.resolve(response);
        });
    },
  },
};
</script>
